import {ChangeDetectionStrategy, Component, computed, inject} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {ChartModule} from 'primeng/chart';
import {lastValueFrom} from 'rxjs';
import {DashboardService, TodoPriorityId} from '../../../../../api';
import {Loader} from '../../../../components/loader/loader';
import {QueryKey} from '../../../../constants/query-key';
import {RouteUri} from '../../../../constants/route-uri';
import {doughnutOptions, readCssVariable} from '../../charts/chart-theme';

@Component({
  selector: 'elcia-dashboard-todo-priority-chart',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChartModule, Loader, TranslatePipe],
  templateUrl: './dashboard-todo-priority-chart.html',
  host: {class: 'block min-w-0'},
})
export class DashboardTodoPriorityChart {
  private readonly dashboardService = inject(DashboardService);
  private readonly translate = inject(TranslateService);

  protected readonly todosLink = '/' + RouteUri.TODOS;

  private readonly statsQuery = injectQuery(() => ({
    queryKey: [QueryKey.DASHBOARD_TODO_STATS],
    queryFn: () => lastValueFrom(this.dashboardService.getDashboardTodoStats()),
  }));

  protected readonly isLoading = computed(() => this.statsQuery.isPending());
  private readonly stats = computed(() => this.statsQuery.data()?.stats);

  protected readonly pendingCount = computed(() => this.stats()?.pendingCount ?? 0);
  protected readonly overdueCount = computed(() => this.stats()?.overdueCount ?? 0);
  protected readonly hasData = computed(() => (this.stats()?.byPriority?.length ?? 0) > 0);

  protected readonly data = computed(() => {
    const slices = this.stats()?.byPriority ?? [];

    return {
      labels: slices.map(slice => this.priorityLabel(slice.priority)),
      datasets: [
        {
          data: slices.map(slice => slice.count),
          // Colours come from TodoPriority.Color so the doughnut matches the pastilles used elsewhere
          // in the todo screens.
          backgroundColor: slices.map(slice => slice.color ?? readCssVariable('--p-surface-300', '#cbd5e1')),
          borderWidth: 0,
        },
      ],
    };
  });

  protected readonly options = computed(() => doughnutOptions());

  private priorityLabel(priority: TodoPriorityId): string {
    return this.translate.instant(`todo-priority.${priority}`);
  }
}
